<script setup lang="ts">
import SignaturePad from "@/common/components/SignaturePad.vue"
import type { PickupAuthorization } from "@/model/backend/pickup-authorization"
import { useShopDialogs } from "@/shop/composables/useShopDialogs"
import { useHttpClient } from "@/vf"
import { computed, ref } from "vue"
import type { AxiosError } from "axios"
import { useCartStore } from "@/shop/store"

const props = withDefaults(
    defineProps<{
        isOrderSpecificPickupAuthorization?: boolean
    }>(),
    {
        isOrderSpecificPickupAuthorization: false,
    },
)

const emit = defineEmits<{
    (e: "created", value: boolean): void
    (e: "cancel"): void
    (e: "saving", value: boolean): void
}>()

const http = useHttpClient()
const dialogs = useShopDialogs()
const cartStore = useCartStore()

const firstName = ref("")
const lastName = ref("")
const dateOfBirth = ref("")
const signature = ref<string | null>(null)
const signaturePadKey = ref(0)
const isSaving = ref(false)
const created = ref(false)
const isSignatureEmpty = ref(true)

const isSavingDisabled = computed(() => {
    return (
        !firstName.value ||
        !lastName.value ||
        !dateOfBirth.value ||
        isSignatureEmpty.value ||
        !agreedAuthorizationTerms.value
    )
})

const agreedAuthorizationTerms = ref(false)
const saveAttempted = ref(false)

async function save() {
    saveAttempted.value = true
    if (isSavingDisabled.value) {
        window.scrollTo({ top: 0, behavior: "smooth" })
        return
    }

    // age verification for order-specific pickup authorizations because it will be created at the end of the checkout and
    // the catch can't handle it this way, so we have to do it this way
    if (props.isOrderSpecificPickupAuthorization) {
        const today = new Date()
        const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
        const parsedDateOfBirth = new Date(dateOfBirth.value)
        // normalize the date of birth to midnight. This avoids off-by-one errors
        const normalizedDateOfBirth = new Date(
            parsedDateOfBirth.getFullYear(),
            parsedDateOfBirth.getMonth(),
            parsedDateOfBirth.getDate(),
        )

        if (isNaN(normalizedDateOfBirth.getTime()) || normalizedDateOfBirth > eighteenYearsAgo) {
            await dialogs.alert(
                "Ungültiges Geburtsdatum",
                "Die bevollmächtigte Person muss mindestens 18 Jahre alt sein.",
            )
            return
        }
    }

    isSaving.value = true
    emit("saving", true)

    if (props.isOrderSpecificPickupAuthorization) {
        cartStore.setOrderSpecificPickupAuthorization({
            firstName: firstName.value,
            lastName: lastName.value,
            dateOfBirth: dateOfBirth.value,
            signature: signature.value!,
        })
        created.value = true
    } else {
        try {
            await http.post<PickupAuthorization>("pickup-authorization/", {
                firstName: firstName.value,
                lastName: lastName.value,
                dateOfBirth: dateOfBirth.value,
                signature: signature.value,
                agreedAuthorizationTerms: agreedAuthorizationTerms.value,
            })
            created.value = true
        } catch (exc) {
            const error = exc as AxiosError<{ error: string }>
            const data = error.response?.data
            const errorMessage = typeof data === "string" ? data : data?.error

            switch (errorMessage) {
                case "invalid date of birth":
                    await dialogs.alert(
                        "Ungültiges Geburtsdatum",
                        "Die bevollmächtigte Person muss mindestens 18 Jahre alt sein.",
                    )
                    break
                case "duplicate found":
                    await dialogs.alert(
                        "Vollmacht existiert bereits",
                        "Es wurde bereits eine Vollmacht für diese Person hinterlegt. Bitte überprüfe deine Eingaben und versuche es erneut.",
                    )
                    break
                default:
                    await dialogs.alert(
                        "Fehler",
                        "Die Vollmacht konnte nicht gespeichert werden. Prüfe bitte deine Eingaben und versuche es erneut.",
                    )
                    break
            }
            isSaving.value = false
            emit("saving", false)
            return
        }
    }

    saveAttempted.value = false

    await dialogs.alert(
        "Vollmacht hinterlegt",
        `Die Vollmacht für ${firstName.value} ${lastName.value} wurde hinterlegt.`,
    )

    firstName.value = ""
    lastName.value = ""
    dateOfBirth.value = ""
    signature.value = null
    agreedAuthorizationTerms.value = false
    signaturePadKey.value++
    isSignatureEmpty.value = true
    isSaving.value = false
    emit("saving", false)
    emit("created", created.value)
}

defineExpose({ save })
</script>

<template>
    <div v-if="saveAttempted && isSavingDisabled" class="alert alert-danger">
        Folgende Felder müssen ausgefüllt werden:
        <ul>
            <li v-if="!firstName">Vorname</li>
            <li v-if="!lastName">Nachname</li>
            <li v-if="!dateOfBirth">Geburtstag</li>
            <li v-if="isSignatureEmpty">Unterschrift</li>
            <li v-if="!agreedAuthorizationTerms">Vollmachtbedingungen</li>
        </ul>
    </div>

    <form @submit.prevent="save">
        <div class="row mt-4 mb-4">
            <div class="col-12 pb-4 pb-lg-0 mb-3">
                Trage hier die Daten der Person ein, die du zur Abholung deiner Bestellung bevollmächtigen möchtest. Bei
                Klick auf "Speichern" wird in deinem Namen eine Vollmacht erstellt.
            </div>
            <div class="col-md-6 pb-4 pb-lg-0">
                <div class="form-group">
                    <label class="form-label">Vorname</label>
                    <input v-model="firstName" type="text" class="form-control" />
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="form-label">Nachname</label>
                    <input v-model="lastName" type="text" class="form-control" />
                </div>
            </div>
        </div>

        <div class="my-4 pb-2">
            <div class="form-group">
                <label class="form-label">Geburtstag</label>
                <input v-model="dateOfBirth" type="date" class="form-control" />
            </div>
        </div>

        <div class="position-relative my-4">
            <label class="form-label signature-label">Deine Unterschrift (Vollmachtgeber)</label>
            <SignaturePad
                :key="signaturePadKey"
                v-model="signature"
                :eraser="false"
                :undo-redo="false"
                @empty="isSignatureEmpty = $event"
            />
        </div>

        <div class="mt-3" :class="{ 'text-danger': !agreedAuthorizationTerms && saveAttempted }">
            <label>
                <input v-model="agreedAuthorizationTerms" type="checkbox" class="mr-2" />
                Die bevollmächtigte Person ist von mir in Kenntnis gesetzt worden, sich bei Abholung der Bestellung mit
                einem gültigen Ausweisdokument (Personalausweis, Reisepass oder Aufenthaltstitel) ausweisen zu müssen.
                Andere Dokumente sind nicht zulässig.
            </label>
        </div>
    </form>
</template>

<style scoped>
:deep(.signature-pad canvas) {
    border: 1px solid #6f7070;
    border-radius: 5px;
}

.signature-label {
    position: absolute;
    top: -0.8em;
    left: 0.75rem;
    background-color: white;
    color: #6f7070;
    font-size: 0.9em;
    padding: 0 0.25rem;
    width: auto;
    z-index: 1;
}
</style>
